import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Breadcrumbs,
  Button,
  PageHeader,
} from '@plataforma-xvia/design-system-es';
// @ts-ignore
import renderHTML from 'react-render-html';
import { Container } from '../../components/container/container';
import { IDepartment } from '../../apis/department';
import { Catalog } from '../../apis/search-catalog';
import { isValidUrl } from '../../util/url';
import styles from '../catalog/styles.module.css';
import { ServiceCarousel } from '../../components/carousel/service-carousel';
import { LocationSection } from '../catalog/components/location-section';
import { CatalogDetail } from '../../apis/detail-catalog';
import { BreadcrumbItem } from '../../components/breadcrumb/breadcrumb';
import { ArrowIcon } from '../catalog/components/icons';
import { ROUTES } from '../../routes';
import { buildAppPath } from '../../utils/basePath';

type DepartmentSection = CatalogDetail['departmentSections'];
type DepartmentTabKey = 'mission' | 'vision' | 'values';

interface DepartmentTabSectionProps {
  tabs: Array<{ id: DepartmentTabKey; label: string; content?: string }>;
  activeTab: DepartmentTabKey;
  onTabChange: (tab: DepartmentTabKey) => void;
  departmentName?: string;
}

const DepartmentTabSection: FC<DepartmentTabSectionProps> = ({
  tabs,
  activeTab,
  onTabChange,
  departmentName,
}) => {
  const TAB_INDICATOR_WIDTH = 224;
  const TAB_GAP = 32;

  const activeTabIndex = useMemo(
    () => tabs.findIndex((tab) => tab.id === activeTab),
    [tabs, activeTab]
  );
  const safeActiveIndex = activeTabIndex >= 0 ? activeTabIndex : 0;

  const tabListRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [indicatorMetrics, setIndicatorMetrics] = useState({
    width: TAB_INDICATOR_WIDTH,
    offset: safeActiveIndex * (TAB_INDICATOR_WIDTH + TAB_GAP),
  });

  const updateIndicatorMetrics = useCallback(() => {
    const listEl = tabListRef.current;
    if (!listEl) {
      return;
    }

    const isCompact = window.innerWidth <= 640;

    if (isCompact) {
      const listWidth = listEl.clientWidth;
      const width = listWidth / tabs.length;
      const offset = width * safeActiveIndex;
      setIndicatorMetrics({ width, offset });
      return;
    }

    const activeButtonWrapper = tabRefs.current[safeActiveIndex];
    if (activeButtonWrapper) {
      const activeRect = activeButtonWrapper.getBoundingClientRect();
      const listRect = listEl.getBoundingClientRect();
      const width = activeRect.width;
      const offset = activeRect.left - listRect.left;
      setIndicatorMetrics({ width, offset });
      return;
    }

    setIndicatorMetrics({
      width: TAB_INDICATOR_WIDTH,
      offset: safeActiveIndex * (TAB_INDICATOR_WIDTH + TAB_GAP),
    });
  }, [safeActiveIndex, tabs.length]);

  useLayoutEffect(() => {
    updateIndicatorMetrics();
  }, [updateIndicatorMetrics, safeActiveIndex, tabs.length]);

  useLayoutEffect(() => {
    const handleResize = () => updateIndicatorMetrics();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicatorMetrics]);

  const indicatorStyle = useMemo(
    () => ({
      width: `${indicatorMetrics.width}px`,
      transform: `translateX(${indicatorMetrics.offset}px)`,
    }),
    [indicatorMetrics]
  );

  const currentTab = tabs[safeActiveIndex];

  return (
    <div className={styles.tabSection}>
      <div className={styles.tabList} ref={tabListRef}>
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              ref={(el) => (tabRefs.current[index] = el)}
              className={styles.tabButtonWrapper}
            >
              <Button
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`${styles.tabButton} ${
                  isActive ? styles.tabButtonActive : ''
                }`}
                variant="text"
                size="none"
                aria-pressed={isActive}
                iconStart={<span className={styles.tabOrder}>{index + 1}</span>}
                label={tab.label}
              />
            </div>
          );
        })}
        <span
          className={styles.tabIndicator}
          style={indicatorStyle}
          aria-hidden
        />
      </div>

      <div className={styles.tabPanel}>
        <div className={styles.infoContent}>
          <h3 className={styles.infoTitle}>
            {currentTab?.label}
            {departmentName ? ` do ${departmentName}` : ''}
          </h3>
          <div className={styles.infoText}>
            {currentTab?.content
              ? renderHTML(currentTab.content)
              : 'Informação não informada.'}
          </div>
        </div>
      </div>
    </div>
  );
};

export interface DepartmentProps extends IDepartment {
  departmentSections?: DepartmentSection;
  catalogList?: Catalog[];
  onClickCatalog: (slug: string, categorySlug?: string) => void;
  departmentServicesRef?: React.Ref<HTMLDivElement>;
  breadcrumbs?: BreadcrumbItem[];
}

export const Department: FC<DepartmentProps> = ({
  name,
  shortName,
  description,
  mission,
  vision,
  values,
  link,
  departmentSections,
  catalogList,
  onClickCatalog,
  departmentServicesRef,
  breadcrumbs,
}) => {
  const [selectedCity, setSelectedCity] = useState<string>();
  const [selectedSection, setSelectedSection] = useState<string>();
  const [activeTab, setActiveTab] = useState<DepartmentTabKey>('mission');

  useEffect(() => {
    if (departmentSections && departmentSections.length > 0) {
      const firstSection = departmentSections[0];
      setSelectedCity(firstSection.city || '');
      setSelectedSection(firstSection.id || firstSection.name);
      return;
    }

    setSelectedCity(undefined);
    setSelectedSection(undefined);
  }, [departmentSections]);

  const tabs: Array<{ id: DepartmentTabKey; label: string; content?: string }> =
    useMemo(
      () => [
        { id: 'mission', label: 'Missão', content: mission },
        { id: 'vision', label: 'Visão', content: vision },
        { id: 'values', label: 'Valores', content: values },
      ],
      [mission, values, vision]
    );

  const handleCityChange = (value: string) => {
    setSelectedCity(value);
    setSelectedSection(undefined);
  };

  const handleSectionChange = (value: string) => {
    setSelectedSection(value);
  };

  const breadcrumbItems = breadcrumbs?.length
    ? breadcrumbs
    : [
        { name: 'Início', path: buildAppPath(ROUTES.HOME_PAGE) },
        {
          name: 'Órgãos e secretarias',
          path: buildAppPath(ROUTES.DEPARTMENT_LIST_PAGE),
        },
        { name },
      ];

  return (
    <div className={`${styles.catalog} ${styles.noMinHeight}`}>
      <PageHeader
        title={shortName || name}
        breadcrumb={
          <Breadcrumbs tone="inverted" showBackButton items={breadcrumbItems} />
        }
        action={
          isValidUrl(link) ? (
            <div className={styles.requestButtonWrapper}>
              <Button
                variant="solid"
                tone="primary"
                size="lg"
                label="Acessar website oficial"
                iconEnd={<ArrowIcon />}
                onClick={() => window.open(link, '_blank')}
              />
            </div>
          ) : undefined
        }
      />

      <Container>
        <div className={styles.mainContent}>
          <div className={styles.contentWrapper}>
            <div className={styles.summarySection}>
              <h2 className={styles.departmentTitle}>{name}</h2>
              {description && (
                <div className={styles.summaryDescription}>
                  {renderHTML(description)}
                </div>
              )}
            </div>

            <DepartmentTabSection
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              departmentName={shortName || name}
            />
          </div>

          <div className={styles.sideContent}>
            {departmentSections && departmentSections.length > 0 && (
              <LocationSection
                departmentSections={departmentSections}
                selectedCity={selectedCity}
                selectedSection={selectedSection}
                onCityChange={handleCityChange}
                onSectionChange={handleSectionChange}
              />
            )}
          </div>
        </div>

        {!!catalogList?.length && (
          <div className={styles.relatedServicesBar}>
            <div
              className={styles.relatedServices}
              ref={departmentServicesRef}
            ></div>
            <div className={styles.relatedServicesInner}>
              <ServiceCarousel
                items={catalogList}
                onClickCatalog={onClickCatalog}
                layout="contained"
              />
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};
